import { useState } from "react";
import { Card, CardContent } from "../ui/card";
import { Button } from "../ui/button";
import { DAYS, DAY_LABELS } from "./constants";
import SignupRosterPopover from "../signups/SignupRosterPopover";
import OptionEditorDialog from "../signups/OptionEditorDialog";

// "Block 1, Block 2" or "All blocks" from a list of covered block keys.
const blocksLabel = (covered) =>
  covered && covered.length
    ? covered
        .map((k) => {
          const m = (k || "").match(/(\d+)/);
          return m ? `Block ${m[1]}` : k === "advisory" ? "Advisory" : k;
        })
        .join(", ")
    : "All blocks";

/**
 * A Trips summary section pinned at the top of the Master Schedule view (all staff).
 * Shows each day's trips with the blocks they cover; clicking a trip opens its roster.
 * Editors can create a new trip (selecting the blocks it spans) via the shared dialog.
 */
export default function TripsSection({
  mode = "week", // "week" | "today"
  signupOverlay,
  dayToDate,
  todayKey,
  canEdit = false,
  staffList = [],
  studentList = [],
  weekDates = [],
  onChanged,
}) {
  const [editorOpen, setEditorOpen] = useState(false);
  const [addDate, setAddDate] = useState("");

  const openAdd = (date) => {
    setAddDate(date || "");
    setEditorOpen(true);
  };

  const tripsFor = (date) => signupOverlay?.by_date?.[date]?.trips || [];

  const cols = mode === "today" ? (todayKey ? [todayKey] : []) : DAYS;
  if (cols.length === 0) return null;

  const anyTrips = cols.some((d) => tripsFor(dayToDate[d]).length > 0);
  // Hide entirely for non-editors when there's nothing to show.
  if (!anyTrips && !canEdit) return null;

  const TripChip = ({ trip, day, date }) => (
    <div
      className="rounded bg-amber-50 border border-amber-200 px-1.5 py-1"
      data-testid={`trips-section-trip-${trip.id}-${day}`}
    >
      <SignupRosterPopover
        option={{ id: trip.id, title: trip.title, lead_staff_name: trip.lead_staff_name, location: trip.location, capacity: null }}
        date={null}
        tripDate={date}
        supportStaffNames={trip.support_staff_names}
        studentCount={(trip.yes_count || 0) + (trip.maybe_count || 0)}
        studentNames={trip.student_names}
        students={canEdit ? studentList : []}
        tone="amber"
        readOnly={!canEdit}
        onChanged={onChanged}
      />
      <div className="text-[9px] text-amber-700 mt-0.5 px-1" data-testid={`trips-section-blocks-${trip.id}-${day}`}>
        Blocks: {blocksLabel(trip.covered_blocks)}
      </div>
    </div>
  );

  return (
    <Card className="no-print border-amber-200" data-testid="trips-section">
      <CardContent className="py-3">
        <div className="flex items-center justify-between mb-2 gap-2 flex-wrap">
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center justify-center w-6 h-6 rounded bg-amber-100 text-amber-700">
              <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 7h-3a2 2 0 0 1-2-2V2"/><path d="M9 18a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h7l4 4v10a2 2 0 0 1-2 2Z"/><path d="M3 7.6v12.8A1.6 1.6 0 0 0 4.6 22h9.8"/></svg>
            </span>
            <h3 className="text-sm font-bold text-amber-800 uppercase tracking-wide">Trips</h3>
          </div>
          {canEdit && (
            <Button
              size="sm"
              variant="outline"
              className="border-amber-300 text-amber-800 hover:bg-amber-100"
              onClick={() => openAdd("")}
              data-testid="trips-section-add-btn"
            >
              + Add Trip
            </Button>
          )}
        </div>

        <div className={mode === "today" ? "grid grid-cols-1 gap-2" : "grid grid-cols-1 sm:grid-cols-5 gap-2"}>
          {cols.map((day) => {
            const date = dayToDate[day];
            const dayTrips = tripsFor(date);
            return (
              <div key={day} className="border rounded-md p-2 min-h-[64px]" data-testid={`trips-section-day-${day}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-semibold text-gray-600">{DAY_LABELS[day]}</span>
                  {canEdit && (
                    <button
                      type="button"
                      onClick={() => openAdd(date)}
                      className="text-[10px] text-amber-700 hover:underline"
                      data-testid={`trips-section-add-${day}`}
                    >
                      + add
                    </button>
                  )}
                </div>
                {dayTrips.length === 0 ? (
                  <p className="text-[11px] text-gray-400 italic">No trips</p>
                ) : (
                  <div className="space-y-1.5">
                    {dayTrips.map((t) => (
                      <TripChip key={`${t.id}-${date}`} trip={t} day={day} date={date} />
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      </CardContent>

      {canEdit && (
        <OptionEditorDialog
          open={editorOpen}
          onOpenChange={setEditorOpen}
          editing={null}
          defaultType="trip"
          allowTypeSwitch={false}
          slots={[]}
          staff={staffList}
          weekDates={weekDates}
          defaults={addDate ? { date: addDate } : {}}
          onSaved={() => {
            setEditorOpen(false);
            onChanged && onChanged();
          }}
        />
      )}
    </Card>
  );
}
